import React from "react";
import { cn } from "@/lib/utils";
import { CheckCircle } from "lucide-react";

interface SignupProgressProps {
  currentStep: number;
  totalSteps: number;
  emailVerified: boolean;
  className?: string;
}

const stepNames = ["Basic Information", "Company Information", "Profile & Skills"];

export const SignupProgress: React.FC<SignupProgressProps> = ({
  currentStep,
  totalSteps,
  emailVerified,
  className,
}) => {
  const progressPercent = ((currentStep - 1) / (totalSteps - 1)) * 100;

  return (
    <div className={cn("w-full max-w-md mx-auto mb-6", className)}>
      {/* Phone: a single line and a thin bar, so the first field is on the
          first screen (#997). The circle stepper below takes ~370px there. */}
      <div className="sm:hidden space-y-2" data-testid="signup-progress-compact">
        <p className="text-sm text-white drop-shadow font-medium">
          Step {currentStep} of {totalSteps}
          <span className="text-white/80"> · {stepNames[currentStep - 1]}</span>
          {currentStep === 1 && emailVerified && (
            <span className="text-green-300"> · Verified</span>
          )}
        </p>
        <div
          className="h-1 rounded-full bg-white/20 overflow-hidden"
          role="progressbar"
          aria-label="Signup progress"
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-valuenow={currentStep}
        >
          <div
            className="h-full bg-white transition-[width] duration-300 ease-in-out"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative hidden sm:block">
        <div className="flex items-center justify-between mb-4">
          {Array.from({ length: totalSteps }).map((_, index) => {
            const stepNumber = index + 1;
            const isCompleted = stepNumber < currentStep || (stepNumber === 1 && emailVerified);
            const isCurrent = stepNumber === currentStep;
            const isAccessible = stepNumber <= currentStep || (stepNumber === 1 && !emailVerified);

            return (
              <div key={stepNumber} className="flex flex-col items-center flex-1">
                {/* Step Circle */}
                <div
                  className={cn(
                    "w-10 h-10 rounded-full border-2 flex items-center justify-center font-bold text-sm transition-all duration-200",
                    {
                      // Completed step
                      "bg-white border-white text-gray-900": isCompleted,
                      // Current step
                      "border-white text-white bg-white/10": isCurrent && !isCompleted,
                      // Future step (not accessible)
                      "border-white/30 text-white/30 bg-white/5": !isAccessible,
                      // Default accessible step
                      "border-white/50 text-white/70 bg-white/10":
                        isAccessible && !isCurrent && !isCompleted,
                    }
                  )}
                >
                  {isCompleted ? <CheckCircle className="w-5 h-5" /> : stepNumber}
                </div>

                {/* Step Title */}
                <span
                  className={cn(
                    "text-xs mt-2 text-center font-bold transition-colors duration-200 drop-shadow",
                    {
                      "text-white": isCompleted || isCurrent,
                      "text-white/70": !isCompleted && !isCurrent,
                    }
                  )}
                >
                  {stepNames[index]}
                </span>

                {/* Email Verification Indicator */}
                {stepNumber === 1 && emailVerified && (
                  <span
                    className={cn(
                      "text-xs mt-1 transition-colors duration-200 text-green-300 drop-shadow font-medium"
                    )}
                  >
                    Verified
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Progress Line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-white/20 -z-10">
          <div
            className="h-full bg-white transition-[width] duration-300 ease-in-out"
            style={{
              width: `${progressPercent}%`,
            }}
          />
        </div>
      </div>

      {/* Progress Text */}
      <div className="text-center hidden sm:block">
        <p className="text-sm text-white/80 drop-shadow font-medium">
          Step {currentStep} of {totalSteps}
        </p>
      </div>
    </div>
  );
};
